import axios, { AxiosError } from 'axios'
import { defaultRequestInterceptors, defaultResponseInterceptors } from './config'

import { AxiosInstance, InternalAxiosRequestConfig, RequestConfig, AxiosResponse } from './types'
import { ElMessage } from 'element-plus'
import { REQUEST_TIMEOUT } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'

export const PATH_URL = import.meta.env.VITE_API_BASE_PATH

const abortControllerMap: Map<string, AbortController> = new Map()

const axiosInstance: AxiosInstance = axios.create({
  timeout: REQUEST_TIMEOUT,
  baseURL: PATH_URL
})

axiosInstance.interceptors.request.use((res: InternalAxiosRequestConfig) => {
  const controller = new AbortController()
  const url = res.url || ''
  res.signal = controller.signal
  abortControllerMap.set(url, controller)
  return res
})

// 处理 401: 尝试刷新 token，失败则跳转登录
let isRefreshing = false
const pendingQueue: Array<(token: string) => void> = []

axiosInstance.interceptors.response.use(
  (res: AxiosResponse) => {
    const url = res.config.url || ''
    abortControllerMap.delete(url)
    return res
  },
  async (error: AxiosError) => {
    const config = error.config as any
    if (error.response?.status === 401 && !config?._retry) {
      config._retry = true
      const userStore = useUserStoreWithOut()
      const refreshToken = userStore.getRefreshToken

      if (!refreshToken) {
        userStore.logout()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          pendingQueue.push((token: string) => {
            config.headers['Authorization'] = token
            resolve(axiosInstance.request(config))
          })
        })
      }

      isRefreshing = true
      try {
        const res = await axios.post(`${PATH_URL}/api/admin/auth/refresh`, null, {
          params: { refreshToken }
        })
        const newAccessToken = res.data?.data?.accessToken
        const newRefreshToken = res.data?.data?.refreshToken
        if (newAccessToken) {
          const bearer = `Bearer ${newAccessToken}`
          userStore.setToken(bearer)
          userStore.setRefreshToken(newRefreshToken)
          pendingQueue.forEach((cb) => cb(bearer))
          pendingQueue.length = 0
          config.headers['Authorization'] = bearer
          return axiosInstance.request(config)
        }
      } catch {
        userStore.logout()
        return Promise.reject(error)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.status !== 401) {
      ElMessage.error((error.response?.data as any)?.message || error.message)
    }
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.request.use(defaultRequestInterceptors)
axiosInstance.interceptors.response.use(defaultResponseInterceptors)

const service = {
  request: (config: RequestConfig) => {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors.requestInterceptors(config as any)
      }

      axiosInstance
        .request(config)
        .then((res) => {
          resolve(res)
        })
        .catch((err: any) => {
          reject(err)
        })
    })
  },
  cancelRequest: (url: string | string[]) => {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  },
  cancelAllRequest() {
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
}

export default service
